import { Card, Grade, Rarity, UserCard } from '../types';

type SlabCardData = Pick<Card, 'pokemon_name' | 'set_name' | 'rarity' | 'grade' | 'image_url' | 'market_price_usd'>;

interface SlabCardProps {
  card: SlabCardData | UserCard;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  badge?: React.ReactNode;
}

const GRADE_COLOR: Record<Grade, string> = {
  PSA_10: '#f59e0b',
  CGC_10: '#c0c0c0',
  RAW: '#3b82f6',
};

const GRADE_LABEL: Record<Grade, string> = {
  PSA_10: '10',
  CGC_10: '10',
  RAW: 'RAW',
};

const GRADE_HEADER: Record<Grade, string> = {
  PSA_10: 'PSA',
  CGC_10: 'CGC',
  RAW: 'RAW',
};

function isUltraGlow(rarity: Rarity, grade: Grade) {
  return rarity === 'ultra-rare' || grade === 'PSA_10' || grade === 'CGC_10';
}

export default function SlabCard({ card, size = 'md', onClick, badge }: SlabCardProps) {
  const glow = isUltraGlow(card.rarity, card.grade);
  const gradeColor = GRADE_COLOR[card.grade];

  const sizeClasses = {
    sm: 'w-full',
    md: 'w-full',
    lg: 'w-full max-w-xs mx-auto',
  };

  return (
    <div
      className={`${sizeClasses[size]} ${onClick ? 'cursor-pointer active:scale-95' : ''} transition-transform`}
      onClick={onClick}
      style={
        glow
          ? { filter: `drop-shadow(0 0 8px ${gradeColor}66)` }
          : undefined
      }
    >
      {/* Slab frame */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          border: '2px solid rgba(255,255,255,0.85)',
          backgroundColor: '#1a1a1f',
        }}
      >
        {/* PSA/CGC header bar */}
        <div
          className="flex items-center justify-between px-2 py-0.5"
          style={{ backgroundColor: card.grade === 'CGC_10' ? '#1a1a5e' : '#cc0000' }}
        >
          <span className="text-white font-black text-xs tracking-widest">
            {GRADE_HEADER[card.grade]}
          </span>
          <span
            className="font-black text-sm leading-none"
            style={{ color: gradeColor }}
          >
            {GRADE_LABEL[card.grade]}
          </span>
        </div>

        {/* Card image */}
        <div className="relative bg-black aspect-[3/4]">
          <img
            src={card.image_url}
            alt={card.pokemon_name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {badge && (
            <div className="absolute top-1 right-1">{badge}</div>
          )}
        </div>

        {/* Grade footer */}
        <div className="px-2 py-1 flex items-center justify-between bg-black/60">
          <div className="min-w-0">
            <p className="text-white text-xs font-bold truncate leading-tight">
              {card.pokemon_name}
            </p>
            <p className="text-gray-400 text-[10px] truncate leading-tight">
              {card.set_name}
            </p>
          </div>
          {card.grade === 'PSA_10' || card.grade === 'CGC_10' ? (
            <span className="text-[9px] font-black tracking-widest ml-1 flex-shrink-0" style={{ color: gradeColor }}>
              GEM MINT
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
